import { useEffect, useRef, useState } from 'react';
import {
  batteryColor,
  formatAge,
  hardwareName,
  isOnline,
  precisionRadiusM,
  roleName,
  signalColor,
  signalLabel,
  type NodeDetail,
} from '@mcoverage/shared';
import { CloseIcon, PauseIcon, PlayIcon } from './icons';

interface NodePanelProps {
  nodeNum: number;
  onClose: () => void;
  /** Shared with the map so the drawn route follows the slider. */
  playhead: number | null;
  onPlayheadChange: (next: number | null) => void;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="kv">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function pct(v: number | null): string {
  return v === null ? '—' : `${v.toFixed(0)}%`;
}

export function NodePanel({ nodeNum, onClose, playhead, onPlayheadChange }: NodePanelProps) {
  const [detail, setDetail] = useState<NodeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setDetail(null);
    setError(null);
    onPlayheadChange(null);
    setPlaying(false);

    fetch(`/api/nodes/${nodeNum}`, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`${r.status}`))))
      .then((d: NodeDetail) => setDetail(d))
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      });

    return () => controller.abort();
    // onPlayheadChange is a plain setState from the parent and is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeNum]);

  const track = detail?.track ?? [];

  useEffect(() => {
    if (!playing || track.length < 2) return;
    timerRef.current = setInterval(() => {
      const next = (playhead === null ? -1 : playhead) + 1;
      if (next >= track.length) {
        setPlaying(false);
        onPlayheadChange(track.length - 1);
        return;
      }
      onPlayheadChange(next);
    }, 350);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [playing, track.length, playhead, onPlayheadChange]);

  if (error) {
    return (
      <section className="node-panel">
        <header>
          <h2>Node</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Schließen">
            <CloseIcon />
          </button>
        </header>
        <p className="hint error">Konnte Node nicht laden ({error}).</p>
      </section>
    );
  }

  if (!detail) {
    return (
      <section className="node-panel">
        <header>
          <h2>Node</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Schließen">
            <CloseIcon />
          </button>
        </header>
        <p className="hint">Lade…</p>
      </section>
    );
  }

  const now = Date.now() / 1000;
  const online = isOnline(detail.lastHeard, now);
  const radius = precisionRadiusM(detail.positionPrecision);
  const head = playhead === null ? track.length - 1 : playhead;
  const headPoint = track[head];
  const named = detail.longName !== '' || detail.shortName !== '';
  const title = detail.longName || detail.shortName || detail.userId;

  return (
    <section className="node-panel" aria-label={`Details für ${title}`}>
      <header>
        <div>
          <h2 className={named ? '' : 'unnamed'}>{title}</h2>
          <p className="sub">
            <span className={online ? 'badge on' : 'badge off'}>
              {online ? 'online' : formatAge(now - detail.lastHeard)}
            </span>
            <span className="muted">{roleName(detail.role) || 'Client'}</span>
          </p>
        </div>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Schließen">
          <CloseIcon />
        </button>
      </header>

      <div className="node-body">
        {!named && (
          <p className="hint">
            Dieser Node hat bisher keine <strong>NodeInfo</strong> gesendet, deshalb ist nur die ID
            bekannt. Meshtastic-Nodes senden NodeInfo nur in langen Intervallen — der Name taucht
            automatisch auf, sobald das Mesh ihn übermittelt.
          </p>
        )}

        <dl className="kv-list">
          <Row label="Node-ID" value={<code>{detail.userId}</code>} />
          <Row
            label="Name"
            value={named ? detail.longName || detail.shortName : <span className="muted">noch nicht übermittelt</span>}
          />
          <Row label="Kurzname" value={detail.shortName || '—'} />
          <Row label="Hardware" value={hardwareName(detail.hardware)} />
          <Row label="Firmware" value={detail.firmwareVersion || '—'} />
          <Row
            label="Batterie"
            value={
              detail.batteryLevel === null ? (
                '—'
              ) : (
                <span style={{ color: batteryColor(detail.batteryLevel) }}>
                  {detail.batteryLevel}% {detail.voltage !== null && `· ${detail.voltage.toFixed(2)} V`}
                </span>
              )
            }
          />
          <Row label="Signal" value={detail.snr !== null ? `SNR ${detail.snr.toFixed(1)} dB` : '—'} />
          <Row
            label="RSSI"
            value={
              detail.rssi !== null ? (
                <span style={{ color: signalColor(detail.rssi) }}>
                  {detail.rssi} dBm · {signalLabel(detail.rssi)}
                </span>
              ) : (
                '—'
              )
            }
          />
          <Row label="Hops" value={detail.hopsAway ?? '—'} />
          <Row label="Kanal" value={detail.modemPreset || '—'} />
          <Row label="Region" value={detail.region || '—'} />
          <Row
            label="Position"
            value={
              detail.lat !== null ? (
                <>
                  {detail.lat.toFixed(5)}, {detail.lon?.toFixed(5)}
                  {detail.altitude !== null && ` · ${detail.altitude} m`}
                </>
              ) : (
                'nicht veröffentlicht'
              )
            }
          />
          <Row
            label="Genauigkeit"
            value={
              radius ? (
                `±${radius >= 1000 ? `${(radius / 1000).toFixed(1)} km` : `${radius} m`} (${detail.positionPrecision} Bit)`
              ) : (
                '—'
              )
            }
          />
          <Row label="Kanalnutzung" value={pct(detail.channelUtilization)} />
          <Row label="Airtime TX" value={pct(detail.airUtilTx)} />
          <Row label="Erst gesehen" value={new Date(detail.firstSeen * 1000).toLocaleDateString('de-DE')} />
          <Row
            label="Zuletzt gehört"
            value={`${new Date(detail.lastHeard * 1000).toLocaleString('de-DE')} (${formatAge(now - detail.lastHeard)})`}
          />
        </dl>

        {track.length > 1 && (
          <div className="track">
            <div className="track-head">
              <h3>Positionsverlauf</h3>
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  if (playing) {
                    setPlaying(false);
                    onPlayheadChange(null);
                  } else {
                    onPlayheadChange(0);
                    setPlaying(true);
                  }
                }}
              >
                {playing ? (
                  <>
                    <PauseIcon /> Stopp
                  </>
                ) : (
                  <>
                    <PlayIcon /> Abspielen
                  </>
                )}
              </button>
            </div>
            <input
              type="range"
              min={0}
              max={track.length - 1}
              value={head}
              onChange={(e) => {
                setPlaying(false);
                onPlayheadChange(Number(e.target.value));
              }}
              aria-label="Position im Verlauf"
            />
            {headPoint && (
              <p className="hint">
                {new Date(headPoint.time * 1000).toLocaleString('de-DE')}
                {headPoint.snr !== null && ` · SNR ${headPoint.snr.toFixed(1)} dB`}
                {headPoint.precision !== null && ` · ${headPoint.precision} Bit`}
              </p>
            )}
            <p className="hint">{track.length} aufgezeichnete Positionen</p>
          </div>
        )}
      </div>
    </section>
  );
}